/**
 * BuildPilot motion primitives (Emil Kowalski–inspired).
 *
 * Goals:
 *   - Subtle, restrained, never bouncy.
 *   - Short durations (160–320 ms) with custom cubic-bezier easings.
 *   - Single source of truth for transitions so the whole site feels
 *     cohesive instead of every component picking random durations.
 *
 * Usage:
 *   import { ease, dur, fadeUp, stagger } from '@/lib/motion';
 *   <motion.div initial="hidden" animate="visible" variants={fadeUp}>...
 */

// ---- Easing curves ---------------------------------------------------------
// `out` curves decelerate at the end (good for entrances).
// `inOut` curves are symmetrical (good for layout shifts).
// `spring` is intentionally NOT exposed - Kowalski-style avoids bounce.
export const ease = {
  // Default decelerate - smooth landings.
  out: [0.22, 1, 0.36, 1],
  // Crisp inOut for height/width changes and shared-layout transitions.
  inOut: [0.65, 0, 0.35, 1],
  // Gentle slow-out used by Apple/Linear.
  apple: [0.32, 0.72, 0, 1],
  // Sharp accelerate - exits.
  in: [0.4, 0, 1, 1],
};

// ---- Durations -------------------------------------------------------------
// Kept tight so the UI feels responsive, never sluggish.
export const dur = {
  micro: 0.16, // hover, focus, tap feedback
  short: 0.22, // small UI reveals
  base: 0.28, // standard entrance
  long: 0.4,  // page transition / large hero
};

// ---- Variants --------------------------------------------------------------
export const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: dur.base, ease: ease.out },
  },
};

export const fadeIn = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: dur.base, ease: ease.out },
  },
};

export const fadeScale = {
  hidden: { opacity: 0, scale: 0.985 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: dur.base, ease: ease.out },
  },
};

// Container that staggers its direct children (each child uses fadeUp).
export const stagger = (delayChildren = 0.04, staggerChildren = 0.06) => ({
  hidden: {},
  visible: {
    transition: { delayChildren, staggerChildren },
  },
});

// Used by mega-menu panel / popovers - short, no translate (avoids
// fighting with absolute positioning).
export const popover = {
  hidden: { opacity: 0, y: -4 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: dur.short, ease: ease.out },
  },
  exit: {
    opacity: 0,
    y: -4,
    transition: { duration: dur.micro, ease: ease.in },
  },
};

// Modal/dialog - slight scale + fade.
export const modal = {
  hidden: { opacity: 0, scale: 0.97, y: 6 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: dur.short, ease: ease.out },
  },
  exit: {
    opacity: 0,
    scale: 0.97,
    y: 6,
    transition: { duration: dur.micro, ease: ease.in },
  },
};

export const backdrop = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: dur.short, ease: ease.out } },
  exit: { opacity: 0, transition: { duration: dur.micro, ease: ease.in } },
};

// Press feedback - apply via whileTap on interactive elements.
export const press = { scale: 0.985 };
// Hover lift - apply via whileHover on cards.
export const lift = { y: -2 };

// Viewport defaults - fire once, slightly inside the visible area.
export const viewportOnce = { once: true, amount: 0.18, margin: '0px 0px -8% 0px' };
